import { useState } from 'react';
import {
  apiErrorSummary,
  deleteOrphanSession,
  estimateWorkoutCalories,
  keyForOutboxItem,
  mintSetIds,
  persistWorkout,
  saveErrorKey,
  sessionVersion,
  unwrapSaveError,
  type AthleteEnergy,
  type EffortScale,
  type Exercise,
  type MovementPattern,
  type PRsByExercise,
  type Workout as WorkoutT,
} from '@heraion/shared';

import { uuidv4 } from './uuid';
import { weightUnitLabels } from '../weightUnitLabels';
import { clearPersistedLoggerState } from './state/loggerDraftStorage';
import type {
  LoggerDispatch,
  LoggerDraft,
  WeightUnit,
} from './state/loggerDraft';
import {
  invalidateEntryHistoryCache,
  snapshotRecentEvidence,
} from './useEntryHistory';
import { buildPayload, detectOutliersForDraft } from './workoutMapping';
import { outlierPromptItems } from './outlierPromptItems';
import type { LoggerMode } from './loggerMode';
import type { EditingTarget, LoggerOnSaved, MaxBumpInput } from './LoggerScreen';
import type { LoggerHostDeps, QueuedWorkout } from './hostDeps';
import { useFlushNoteEvents } from './useFlushNoteEvents';
import { openSaveWriter, saveFailureMessage } from './saveWriter';
import type { PostSaveSummaryState } from './usePostSaveSummary';

// Save flow extracted verbatim out of LoggerScreen (Track R slice A).
// Owns the `submitting` flag + the proceed/onSave logic; the error,
// serverError, and postSaveSummary states stay in LoggerScreen (read by
// the render tree and cleared by the hydration effect) and are written
// through the setters passed in here. Behaviour-preserving — no path
// changed, only relocated.

const GONE_KEY = 'mobile.logger.errCorrectionGone';
/** The host-with-no-queue wording: points at the button, not at the phone. */
export const GONE_HERE_KEY = 'mobile.logger.errCorrectionGoneHere';


export type UseSaveWorkoutParams = {
  draft: LoggerDraft;
  editing: EditingTarget | null;
  mode: LoggerMode;
  catalog: Exercise[] | null;
  prHistory: PRsByExercise | null;
  defaultUnit: WeightUnit;
  effortScale: EffortScale;
  /** The athlete's energy demographics (bodyweight/age/sex), resolved from the
   *  Profile by the caller (resolveAthleteEnergy). Feeds the HC-5 workout
   *  calorie estimate persisted at save; absent fields degrade it honestly. */
  athlete: AthleteEnergy;
  /** i18n.resolvedLanguage — used for the outlier-prompt weight format. */
  resolvedLanguage: string | undefined;
  /** Auto-name suggestion ([[workoutNameSuggest]]): the fallback persisted
   *  as Workout.name when the user left the name blank. */
  nameSuggestion: string;
  dispatch: LoggerDispatch;
  persistedHydratedRef: { current: boolean };
  onSaved: LoggerOnSaved;
  onClose: () => void;
  /** recordsResource.refresh — refreshes the cached PR/records view. */
  refreshRecords: () => unknown;
  /** useStopAllTimers: stop every timer (rest, hold, bout) and its
   *  notifications when the workout ends. The sent-save path
   *  keeps the modal open in summary mode and defers the draft reset
   *  to the Done CTA, so the draft.startedAt effect (which tears the
   *  timer down on discard/queued) never fires here — the timer would
   *  otherwise keep ticking under the summary card. */
  stopAllTimers: () => void;
  setError: (v: string | null) => void;
  setServerError: (v: string | null) => void;
  setPostSaveSummary: (v: PostSaveSummaryState | null) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  deps: LoggerHostDeps;
};

export function useSaveWorkout({
  draft,
  editing,
  mode,
  catalog,
  prHistory,
  defaultUnit,
  effortScale,
  athlete,
  resolvedLanguage,
  nameSuggestion,
  dispatch,
  persistedHydratedRef,
  onSaved,
  onClose,
  refreshRecords,
  stopAllTimers,
  setError,
  setServerError,
  setPostSaveSummary,
  t,
  deps,
}: UseSaveWorkoutParams) {
  const [submitting, setSubmitting] = useState(false);
  const unitLabels = weightUnitLabels(t);

  // The timeline notes, written once the workout is saved (#24).
  const flushNoteEvents = useFlushNoteEvents({ draft, editing, deps });

  // Persist a validated payload to the server (or the outbox on a
  // genuine network failure). Split out of onSave so the outlier-
  // warning Alert can re-enter the save flow when the user picks
  // "Save anyway" — buildPayload re-validation would be redundant by
  // then, the draft is the same as when the prompt opened.
  const proceedWithSave = async (
    built: Extract<ReturnType<typeof buildPayload>, { ok: true }>,
    asNew = false,
  ) => {
    setSubmitting(true);
    // #42: one account for the send and the queued row (./saveWriter).
    const opened = openSaveWriter(deps);
    if ('error' in opened) {
      setError(opened.error);
      setSubmitting(false);
      return;
    }
    const { writer } = opened;
    // This attempt's id: a new workout's session id, and the seed of the
    // queued row's replay keys either way.
    const id = uuidv4();
    // On an edit, carry the original session's createdAt on the payload.
    // A correction rewrites the session in place (#38) and its PATCH does
    // not send createdAt, so the server keeps its own either way. Repeat
    // clones into a genuinely new session and keeps a fresh createdAt.
    // `mode === 'log-past'` already implies a non-null editing target (see
    // deriveLoggerMode), but the compiler cannot see through the derivation:
    // the null check narrows `editing` for the reads below, it does not
    // re-derive the mode.
    // ⛔ NO `mode === 'prescribe'` branch here, deliberately (PE-22 K0), and
    // the omission is the declaration. A prescription is never saved through
    // this hook: it is authored into the PROGRAM payload (`SetIntensity`, via
    // the mount's onPrescribeEffort — see useEffortCommit), not POSTed as a
    // Workout. So the question this hook must answer in 'prescribe' is "how
    // did you get here", and a local guard would answer it by swallowing the
    // wiring mistake that brought us — the failure mode the house already
    // named one floor down (useSetCyclePill, same shape).
    // [[feedback_a_fail_closed_guard_hides_the_bug_above_it]]
    // `asNew` (#38): the correction's session is gone, so the open draft is
    // saved as a NEW workout, exactly like a fresh log: no edit reference.
    const isEdit = !asNew && editing !== null && mode === 'log-past';
    // A correction keeps the session's id (#38, model A).
    const sessionId = isEdit ? editing.sessionId : id;
    const payload =
      isEdit && editing.workout.createdAt
        ? { ...built.payload, createdAt: editing.workout.createdAt }
        : built.payload;
    // Movement pattern per exercise (from the draft entries, populated at
    // picker time) — used both for the HC-5 calorie estimate here and the
    // post-save MAX-bump eval below, so build it once.
    const exercisePatterns: Record<string, MovementPattern | null> = {};
    for (const e of draft.entries) {
      exercisePatterns[e.exerciseId] = e.movementPattern;
    }
    // HC-5: compute JL's own calorie estimate at save (uses the athlete's
    // then-current bodyweight) and persist it beside the measured value. Null
    // when nothing is estimable — then the field is simply absent.
    const caloriesEstimate = estimateWorkoutCalories(
      built.payload,
      exercisePatterns,
      athlete,
    );
    const workoutToPersist = caloriesEstimate
      ? { ...payload, caloriesEstimate }
      : payload;
    // A new workout's set ids, minted once: the direct save and the queued
    // row send the same ones, so a replay never stores a set twice (#286).
    // A correction carries the version the editor loaded (#314). Built on a
    // queued one, it takes the version that one left once it landed; until
    // then (`turn` null) it waits behind it, in the queue.
    const turn =
      isEdit && editing.queuedOn !== undefined
        ? (writer.correctionTurn?.(editing.queuedOn) ?? null)
        : undefined;
    const queued: QueuedWorkout = isEdit
      ? {
          id,
          workout: workoutToPersist,
          editingId: editing.sessionId,
          baseVersion: turn ? turn.baseVersion : sessionVersion(editing.workout),
          supersedes: turn === null ? editing.queuedOn : undefined,
        }
      : { id, workout: workoutToPersist, editingId: null, setIds: mintSetIds(workoutToPersist) };
    // Build a Workout-shaped object from the about-to-be-saved payload for
    // the post-save MAX-bump evaluation. Server-stamped fields (profileId,
    // createdAt, updatedAt) get placeholder values — the evaluator only
    // walks `entries[].sets`, so the placeholders are immaterial. The
    // exercise pattern map comes from the draft entries (populated at
    // picker time, see DraftEntry); entries without a pattern fall through
    // to the default rep-trust cap in the MAX-suggest functions.
    //
    // ⚠️ Built HERE, above the try, and not in the success branch: the
    // offline branch needs the identical object (LG-35 — a workout saved in
    // airplane mode used to offer no MAX prompt at all, because
    // onSaved('queued') carried no maxBumpInput). Nothing in it depends on
    // the server's answer, so one construction serves both paths. Two
    // copies of this field list would be the classic pair that diverges.
    const justSavedWorkout: WorkoutT = {
      id: sessionId,
      profileId: '',
      startedAt: built.payload.startedAt,
      ...(built.payload.endedAt ? { endedAt: built.payload.endedAt } : {}),
      ...(built.payload.name ? { name: built.payload.name } : {}),
      entries: built.payload.entries,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    // Snapshot the recent best e1RM per exercise BEFORE either branch
    // invalidates the history cache — the post-save MAX-regression gate
    // needs the pre-save window (recent PAST sessions, not the just-saved
    // one) so one light day can't lower a MAX below a recent stronger
    // session (Antenore 2026-06-26). Cold cache → empty map → legacy
    // behaviour.
    // `MX-11` — ONE walk gives both halves: the best, and how many separate
    // sessions produced one. Splitting them into two snapshots would walk the
    // same window twice and let the two disagree about what qualified.
    const recentEvidence = snapshotRecentEvidence(Date.now());
    const recentBestE1RM: Record<string, number> = {};
    const recentSessionCount: Record<string, number> = {};
    for (const [slug, ev] of Object.entries(recentEvidence)) {
      recentBestE1RM[slug] = ev.bestE1RM;
      recentSessionCount[slug] = ev.sessions;
    }
    const maxBumpInput: MaxBumpInput = {
      workout: justSavedWorkout,
      exercisePatterns,
      workoutId: sessionId,
      recentBestE1RM,
      recentSessionCount,
    };
    // The queued path: the draft goes, the queue keeps the save. A session a
    // failed direct save left is deleted unless the queue took it (#289).
    const queueSave = async (dropLeftSession: () => Promise<void>) => {
      // Enqueue with the same id so a future drain replays through the
      // API's idempotency middleware with stable keys derived from it.
      let inQueue = false;
      try {
        const carded = (await writer.queueWorkout(queued)) === 'carded';
        inQueue = true;
        // Queue the notes too (same session id) so they ride the same
        // reconnect drain as the offline workout, before the reset.
        await flushNoteEvents(sessionId, writer, asNew);
        dispatch({ type: 'reset' });
        stopAllTimers();
        // ⭐ LG-76: awaited, same reason as the Done CTA above.
        await clearPersistedLoggerState();
        persistedHydratedRef.current = false;
        // Queued saves don't reach the server yet, but the draft is
        // gone from the UI: invalidate so the next Logger open
        // doesn't carry stale "this slug has no history" memory if
        // the outbox drains in between.
        invalidateEntryHistoryCache();
        // LG-35: the MAX prompt rides the offline path too. It used to
        // fire without maxBumpInput, so evaluateAfterSave bailed and a
        // workout logged in airplane mode never offered a new MAX at all
        // and the athlete had to notice and set it by hand, or lose it.
        // The evaluation reads the in-memory maxes cache, so it works
        // whenever the app has loaded them at some point this session;
        // a cold boot with no signal still skips it, deliberately (see
        // the maxesLoaded guard in useMaxBumpFlow).
        // Into a recovery card, not the queue (#314): no MAX prompt for a
        // workout that did not land, and the athlete is told where it is.
        onSaved('queued', carded ? undefined : maxBumpInput);
        if (carded) deps.notify(t('mobile.recovery.title'), t('mobile.logger.correctionToBell'));
        onClose();
      } catch (enqueueErr) {
        // Not queued: the draft stays and its next save starts over.
        if (!inQueue) await dropLeftSession();
        setError(saveFailureMessage(enqueueErr));
      }
    };
    // Built on a correction still queued (#314): it waits behind that one.
    if (queued.supersedes !== undefined) {
      await queueSave(async () => {});
      setSubmitting(false);
      return;
    }
    try {
      // Try direct first — same model the web uses. On success, close
      // and let the caller refresh the list. On ApiError (server
      // replied with a 4xx/5xx), surface the actual server message
      // INLINE so the user can act on it instead of seeing a generic
      // "retry when online" that no retry will ever clear.
      await persistWorkout({
        workout: workoutToPersist,
        editingId: queued.editingId,
        baseVersion: queued.baseVersion,
        apiFetch: writer.apiFetch,
        generateId: () => id,
        setIds: queued.setIds,
        // A correction's key is its queued row's (#314): a replay after a
        // lost answer gets the answer, not a 412 on its own write.
        ...(isEdit ? { idempotencyKeyFor: keyForOutboxItem(id) } : {}),
        // The catch below decides about a session a failure left (#289).
        cleanupByCaller: true,
      });
      // Persist the timeline notes against the saved session id, before
      // the deferred draft reset wipes them.
      await flushNoteEvents(sessionId, writer, asNew);
      // ⭐ LG-53 — the persisted slot stops representing unsaved work HERE,
      // not at the Done CTA below. It used to survive until the athlete
      // tapped Done, so a process death in between left a draft with
      // inProgress:true; the cold-boot path offered Resume, the athlete
      // saved again, and this function minted a FRESH uuidv4 that walked
      // straight past the API's attribute_not_exists guard. Three duplicate
      // pairs reached production that way. The clear is placed after the
      // note flush on purpose: until the notes are in their own outbox the
      // slot is still the only copy of them.
      // ⛔ The mirror in useDraftPersistence would put the blob right back —
      // the modal stays visible in summary mode — so this clear only holds
      // because that effect (and the background flush beside it) now return
      // early once postSaveSummary is set. One without the other is cosmetic.
      await clearPersistedLoggerState();
      // Stay open in summary mode. Defer the reset + persistence
      // teardown + onClose to the Done CTA so the PostSessionSummaryCard
      // can read the draft-less server state via useSessionResult.
      // invalidateEntryHistoryCache + recordsResource.refresh run now
      // so the cached views the user navigates to (after Done) already
      // reflect this save. onSaved fires here too — the workouts-list
      // refresh is independent of the user's summary-mode dismissal.
      invalidateEntryHistoryCache();
      // The server recomputes this session's SessionResult on save, so drop
      // the shared progress cache too → the Progress panels refetch fresh
      // when next focused (perf B2).
      deps.invalidateSessionResults();
      void refreshRecords();
      // Workout ended — stop the rest timer now (banner + notification),
      // not at the deferred Done CTA, so it doesn't keep ticking under
      // the summary card.
      stopAllTimers();
      const maxProposed = onSaved('sent', maxBumpInput) ?? [];
      setPostSaveSummary({
        sessionId,
        maxProposed,
        // ⭐ LG-76 — awaited before onClose: closing lets the next workout open,
        // and a plan start now READS the slot before it seeds. An unawaited
        // clear can leave this saved draft there to be resurrected over it.
        // ⛔ The clear below is NOT redundant with LG-53's above: the reset
        // dispatch on the line before it changes the draft, which re-fires
        // the mirror effect. It returns early today because postSaveSummary
        // is still set at that instant — this awaited clear is what keeps
        // the slot empty if that ordering ever stops holding.
        onDone: () => {
          void (async () => {
            dispatch({ type: 'reset' });
            await clearPersistedLoggerState();
            persistedHydratedRef.current = false;
            setPostSaveSummary(null);
            onClose();
          })();
        },
      });
      setSubmitting(false);
      return;
    } catch (thrown) {
      // A session the failed save left on the server is deleted unless the
      // queue takes the save, whose replay completes it by id (#289).
      const { error: err, leftSession } = unwrapSaveError(thrown);
      const dropLeftSession = async () => {
        if (leftSession !== null) await deleteOrphanSession(writer.apiFetch, leftSession);
      };
      // ⭐ LG-31 — the default is QUEUE, and the direction is the fix.
      // This used to read `if (isNetworkError(err))`, i.e. queue only when
      // we were POSITIVE it was the network. But isNetworkError opens by
      // rejecting every ApiError, and offline apiFetch throws a synthetic
      // ApiError(401, 'not_authenticated') as soon as getIdToken() can't
      // reach Cognito to refresh — so the queue branch was skipped and the
      // athlete got a server error for a workout the server never saw.
      // Leonardo hit exactly this on stable 4.13.1 (2026-08-07).
      // Now: queue unless a server actually returned a deterministic
      // verdict. A queue too many costs one idempotent replay; a queue too
      // few costs a training session.
      if (deps.shouldQueueError(err)) {
        await queueSave(dropLeftSession);
      } else {
        // A deterministic server verdict (400/404/422…). Replaying won't
        // clear it, so surface the actual server message INLINE — the user
        // can act on it, instead of a generic "retry when online" that no
        // retry will ever satisfy.
        //
        // ⚠️ This branch also takes 409 duplicate_id, and that is deliberate
        // — do NOT align it with writeOrQueue, where 409 means `done`. That
        // reading holds for a single-request write; a workout is two.
        // persistWorkout flips `sessionPosted` only after the session POST
        // resolves, so a 409 there means the sets loop never ran and this
        // attempt wrote no sets: "already exists" is not "saved". A new
        // workout's session id is a fresh uuid per attempt, and a
        // correction's 409 is a SessionConflictError (#38). Pinned in
        // useSaveWorkout.test.tsx.
        // A refused set too (#289): the draft stays whole, and is saved again.
        await dropLeftSession();
        // A refusal the athlete can act on reads as a sentence (#38).
        const key = saveErrorKey(err);
        if (key === GONE_KEY && deps.offerSaveAsNewWhenGone) setError(GONE_HERE_KEY);
        else if (key) setError(key);
        else setServerError(apiErrorSummary(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const runSave = async (asNew: boolean) => {
    if (submitting) return;
    setError(null);
    setServerError(null);
    // Drop any set the athlete never marked done. The timeline FocusPanel
    // (the sole logger) always shows a "next set" input that may never be
    // LOG-SET; that proposed-but-not-done set must not be saved (Antenore
    // 2026-05-30; design §7.3).
    const built = buildPayload(draft, effortScale, {
      dropUncompleted: true,
      fallbackName: nameSuggestion,
    });
    if (!built.ok) {
      // Surface via Alert too — the inline error banner renders at the
      // bottom of the ScrollView while the Save button lives in the
      // header, so on validation failure the banner is off-screen and
      // the button "looks dead" (button-refactor regression). The
      // banner stays for anyone who scrolls; the Alert guarantees the
      // feedback reaches the user where their attention is.
      const message = built.reason.startsWith('mobile.logger.')
        ? t(built.reason)
        : built.reason;
      setError(built.reason);
      deps.notify(t('workout.savePromptTitle'), message);
      return;
    }
    // Outlier-weight prompt at save time. Two tiers (1.45.0):
    // history-based (3× the lifter's recent best for that exercise,
    // from /api/me/records — same aggregate driving the gold-pill PR
    // check) and an absolute equipment-ceiling fallback that catches
    // brand-new exercises where prHistory has nothing to compare
    // against. The 999 kg DB row that the old gate let through
    // silently now trips the dumbbell ceiling. Edit mode passes a
    // null prHistory by design (avoids self-comparison false
    // negatives on the PR check); the absolute fallback still fires
    // there so a typo in an edit is caught the same way.
    const outliers = detectOutliersForDraft(draft, prHistory, catalog);
    if (outliers.length > 0) {
      const locale = resolvedLanguage ?? 'en';
      const items = outlierPromptItems(outliers, t, locale, defaultUnit, unitLabels);
      const message = `${t('workout.outlierPromptBody', {
        count: outliers.length,
      })}\n\n• ${items}`;
      const proceedAnyway = await deps.confirm({
        title: t('workout.savePromptTitle'),
        body: message,
        confirmLabel: t('workout.savePromptAnyway'),
        cancelLabel: t('workout.savePromptContinue'),
      });
      if (proceedAnyway) void proceedWithSave(built, asNew);
      return;
    }
    await proceedWithSave(built, asNew);
  };

  return {
    submitting,
    onSave: () => runSave(false),
    onSaveAsNew: () => runSave(true),
  };
}
